import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Loader2, PartyPopper } from "lucide-react";
import { API } from "@/lib/assets";
import { useLang } from "@/lib/i18n";

export default function Waitlist() {
  const { t } = useLang();
  const w = t.waitlist;
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { handle, position }
  const debounce = useRef(null);

  const normalized = handle.trim().replace(/^@/, "").toLowerCase();
  const validFormat = /^[a-z0-9_]{3,20}$/.test(normalized);

  useEffect(() => {
    if (!handle) { setStatus(null); return; }
    if (!validFormat) { setStatus("invalid"); return; }
    setStatus("checking");
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/waitlist/check`, { params: { handle: normalized } });
        setStatus(data.available ? "available" : "taken");
      } catch {
        setStatus(null);
      }
    }, 450);
    return () => clearTimeout(debounce.current);
  }, [handle]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validFormat) { toast.error(w.toastInvalidHandle); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error(w.toastInvalidEmail); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { handle: normalized, email });
      setSuccess({ handle: data.handle, position: data.position });
      toast.success(w.toastSuccess(data.handle, data.position));
    } catch (err) {
      const msg = err?.response?.status === 409 ? w.toastTaken : (err?.response?.data?.detail || w.toastGeneric);
      toast.error(msg);
      if (err?.response?.status === 409) setStatus("taken");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reserve" className="py-24 md:py-32 border-b border-white/10">
      <div className="mx-auto max-w-[1000px] px-5 md:px-10 text-center">
        <p className="font-mono-pw uppercase text-xs tracking-[0.3em] text-[#E6FF00] mb-6">{w.overline}</p>
        <h2 className="font-anton uppercase leading-[0.86] text-5xl md:text-8xl mb-6">
          {w.headA} <br /> <span className="pw-outline-text">{w.headB}</span>
        </h2>
        <p className="font-body text-white/60 max-w-lg mx-auto mb-12">
          {w.subPre}<span className="text-white">{w.subLink}</span>{w.subPost}
        </p>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="waitlist-success"
              className="border border-[#E6FF00] bg-[#111111] p-10 max-w-lg mx-auto"
            >
              <PartyPopper className="text-[#E6FF00] mx-auto mb-5" size={40} />
              <h3 className="font-anton uppercase text-3xl md:text-4xl mb-3">
                @{success.handle} <span className="text-[#E6FF00]">{w.successTitleSuffix}</span>
              </h3>
              <p className="font-body text-white/70 mb-6">
                {w.successPre}
              </p>
              <div className="font-mono-pw uppercase text-xs tracking-[0.2em] text-white/40">
                pinwin.social/@{success.handle}
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="waitlist-form"
              className="max-w-lg mx-auto text-left"
            >
              <label className="font-mono-pw uppercase text-[10px] tracking-[0.2em] text-white/40 block mb-2">{w.handleLabel}</label>
              <div className="relative mb-4">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-anton text-xl text-[#E6FF00]">@</span>
                <input
                  data-testid="handle-input"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={w.handlePlaceholder}
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#E6FF00] outline-none rounded-none pl-10 pr-12 py-4 font-body text-white placeholder:text-white/25 transition-colors"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  {status === "checking" && <Loader2 className="animate-spin text-white/40" size={18} />}
                  {status === "available" && <Check className="text-[#39FF14]" size={20} data-testid="handle-available" />}
                  {(status === "taken" || status === "invalid") && <X className="text-[#FF3B30]" size={20} data-testid="handle-unavailable" />}
                </div>
              </div>
              {status === "taken" && <p className="font-mono-pw text-xs text-[#FF3B30] -mt-2 mb-4">{w.taken}</p>}
              {status === "invalid" && <p className="font-mono-pw text-xs text-[#FF3B30] -mt-2 mb-4">{w.invalid}</p>}
              {status === "available" && <p className="font-mono-pw text-xs text-[#39FF14] -mt-2 mb-4">{w.available}</p>}

              <label className="font-mono-pw uppercase text-[10px] tracking-[0.2em] text-white/40 block mb-2">{w.emailLabel}</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={w.emailPlaceholder}
                className="w-full bg-[#111111] border border-white/15 focus:border-[#E6FF00] outline-none rounded-none px-4 py-4 font-body text-white placeholder:text-white/25 mb-6 transition-colors"
              />

              <button
                type="submit"
                disabled={submitting || status === "taken" || status === "invalid"}
                data-testid="waitlist-submit-button"
                className="w-full font-mono-pw uppercase text-sm tracking-[0.15em] bg-[#E6FF00] text-black py-4 font-semibold hover:-translate-y-1 transition-transform duration-200 shadow-[5px_5px_0px_rgba(230,255,0,0.3)] disabled:opacity-40 disabled:translate-y-0 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : w.submit}
              </button>
              <p className="font-mono-pw text-[10px] text-white/30 mt-4 text-center tracking-[0.15em] uppercase">
                {w.footnote}
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
